import type { MouseEvent } from 'react';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PARTNER_MODULE_ID } from 'src/components/layouts/PDP/elementIds';
import { trackOpenContactPartnerModal } from 'src/components/molecules/ContactPartnerModal/tracking/contactPartnerModalEvents';
import type { StoryImage } from 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModule';
import {
  trackPartnerImpression,
  trackStoryClick,
  trackVisitShopClick,
} from 'src/components/molecules/PartnerMaxiModule/tracking/partnerMaxiModuleEvents';
import { actions } from 'src/redux/contactPartner/slice';
import {
  selectIsPartnerDataLoaded,
  selectPartnerContent,
  selectPartnerInsights,
} from 'src/redux/partner/selectors';
import { requestPartnerContent, requestPartnerInsights } from 'src/redux/partner/thunks';
import { selectProduct } from 'src/redux/product/selectors';
import type { AppThunkDispatch } from 'src/redux/store';

/**
 * View model for the partner section (Mini and Maxi modules). Owns the partner
 * data reads, the after-hydration fetch, and the interaction/tracking handlers,
 * so the presentational modules stay pure props and the page layout only has to
 * place them.
 */
export interface PartnerViewModel {
  /** In-page anchor to the partner module section (Mini "sold by" link) */
  anchorHref: string;
  dateJoined?: string;
  /** True once both the content and insights requests have settled (success or failure) */
  isDataLoaded: boolean;
  isTopBrand: boolean;
  location: string;
  logoSrc: string;
  name: string;
  numberOfOrders?: number;
  onContactPartnerClick: () => void;
  onImpression: () => void;
  onStoryClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  onVisitShopClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  rating?: number;
  shortcode: string;
  /** Partner storefront URL (Maxi visit-shop / view-story links) */
  storefrontHref: string;
  storyImages: StoryImage[];
  storySummary: string;
  storyText: string;
}

export const usePartnerViewModel = (): PartnerViewModel => {
  const dispatch = useDispatch();
  const product = useSelector(selectProduct);
  const partnerContent = useSelector(selectPartnerContent);
  const partnerInsights = useSelector(selectPartnerInsights);
  const isDataLoaded = useSelector(selectIsPartnerDataLoaded);

  const { shortcode } = product.partner;
  const name = product.partner.name ?? '';

  // Partner content and insights feed several parts of the page, so the fetch is
  // a page-level data dependency rather than something a single component's
  // visibility owns. Load it once after hydration so the data is available
  // regardless of where the user lands or how far they scroll.
  useEffect(() => {
    (dispatch as AppThunkDispatch)(requestPartnerContent({ shortcode }));
    (dispatch as AppThunkDispatch)(requestPartnerInsights({ shortcode }));
  }, [dispatch, shortcode]);

  // The partner modules feed these straight into useImpressionRef and click
  // handlers, and useImpressionRef re-creates its IntersectionObserver whenever
  // its callback identity changes, so these must stay stable across renders.
  const onContactPartnerClick = useCallback(() => {
    dispatch(actions.openContactPartnerModal());
    trackOpenContactPartnerModal(name);
  }, [dispatch, name]);
  const onImpression = useCallback(() => trackPartnerImpression(shortcode), [shortcode]);
  const onVisitShopClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => trackVisitShopClick(e, shortcode),
    [shortcode],
  );

  return {
    anchorHref: `#${PARTNER_MODULE_ID}`,
    dateJoined: partnerContent?.startDate,
    isDataLoaded,
    isTopBrand: Boolean(partnerInsights?.starBrand),
    location: partnerContent?.location ?? '',
    logoSrc: partnerContent?.logo?.src ?? '',
    name,
    numberOfOrders: partnerInsights?.numberOfSales,
    onContactPartnerClick,
    onImpression,
    onStoryClick: trackStoryClick,
    onVisitShopClick,
    rating: partnerInsights?.averageProductRating ?? undefined,
    shortcode,
    storefrontHref: `/partners/${shortcode}`,
    storyImages: partnerContent?.storyImages ?? [],
    storySummary: partnerContent?.storySummary ?? '',
    storyText: partnerContent?.storyText ?? '',
  };
};
